// src/context/AuthContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

API.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

/* =========================================================
   EMAIL VALIDATION LOGIC
========================================================= */

const BLOCKED_DOMAINS = [
  'tempmail.com', 'guerrillamail.com', 'sharklasers.com', 
  'yopmail.com', 'mailinator.com', '10minutemail.com',
  'trashmail.com', 'incognitomail.com', 'getairmail.com',
  'throwawaymail.com', 'maildrop.cc', 'getnada.com'
];

const isRandomString = (str) => {
  if (str.length < 5) return false;
  if (/(.)\1{4,}/.test(str)) return true;
  const sequences = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890', 'qazwsxedc'];
  const lowerStr = str.toLowerCase();
  for (let seq of sequences) {
    if (seq.includes(lowerStr)) return true;
  }
  if (/^[a-z]{2,6}\d{2,4}$/.test(lowerStr)) return true;
  return false;
};

export const isGenericEmail = (email) => {
  if (!email) return true;
  const parts = email.split('@');
  if (parts.length !== 2) return true;
  const localPart = parts[0].toLowerCase();
  const domain = parts[1].toLowerCase();
  if (BLOCKED_DOMAINS.includes(domain)) return true;
  if (isRandomString(localPart)) return true;
  return false;
};

/* =========================================================
   CONTEXT
========================================================= */

const AuthContext = createContext(null);

/* =========================================================
   PROVIDER
========================================================= */

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const logout = useCallback(() => {
    try {
      setCurrentUser(null);
      setToken(null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Clear any other auth-related data
      localStorage.removeItem("pushSubscription");
      navigate("/auth", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      // Force navigation even if there's an error
      navigate("/auth", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");

        if (storedToken && storedUser) {
          setToken(storedToken);
          setCurrentUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        localStorage.clear();
      } finally {
        setInitialized(true);
      }
    };

    initializeAuth();
  }, [logout]);

  const fetchAndStoreUser = async (token) => {
    try {
      const response = await API.get("/users/me", {
        headers: { Authorization: `Bearer ${token}` },
        // Runs right after login / session restore — give cold starts extra room
        timeout: 60000
      });
      const freshUserData = response.data;
      
      if (freshUserData.is_deleted || freshUserData.is_active === false) {
        console.warn("User account is suspended or deleted. Logging out.");
        return { suspended: true };
      }
      
      setCurrentUser(freshUserData);
      localStorage.setItem("user", JSON.stringify(freshUserData));
      return { suspended: false, user: freshUserData };

    } catch (error) {
      console.error("Failed to fetch user profile", error);
      if (error?.response?.status === 503) {
        return { maintenance: true };
      }
      return null;
    }
  };

  const login = async (email, password) => {
    setLoading(true);

    try {
      // 60s: a sleeping Render instance can take longer than axios' default 30s
      // to respond on the first hit; the silent warm-up ping on page load
      // usually makes this invisible.
      const response = await API.post("/auth/login", { email, password }, { timeout: 60000 });
      const { access_token } = response.data;

      localStorage.setItem("token", access_token);
      setToken(access_token);

      const profileResult = await fetchAndStoreUser(access_token);

      if (profileResult?.suspended) {
        logout();
        return { success: false, error: "Your account has been suspended or deleted." };
      }

      if (profileResult?.maintenance) {
        logout();
        return { success: false, error: "Site is currently under maintenance. Please try again later." };
      }

      if (!profileResult || !profileResult.user) {
        return { success: false, error: "Could not verify user profile." };
      }

      return { success: true, user: profileResult.user };

    } catch (error) {
      if (error?.response?.status === 503) {
        return { 
          success: false, 
          error: error?.response?.data?.detail || "Site is currently under maintenance." 
        };
      }

      if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
        return {
          success: false,
          error: "The server is still waking up. Please wait a few seconds and try again.",
        };
      }

      return {
        success: false,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error.message ||
          "Login failed",
      };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    setLoading(true);
    
    try {
      // Note: This client-side check is for UX. 
      // You MUST also validate this on the backend for security.
      if (isGenericEmail(email)) {
        return { 
          success: false, 
          error: "Please use a real email address. Temporary or randomly generated emails are not allowed." 
        };
      }

      await API.post("/auth/signup", {
        name,
        email,
        password
      }, { timeout: 60000 });

      const loginResult = await login(email, password);

      if (loginResult.success) {
        return { success: true, user: loginResult.user };
      } else {
        return { success: false, error: "Account created, but auto-login failed. Please login manually." };
      }

    } catch (error) {
      if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
        return {
          success: false,
          error: "The server is still waking up. Please wait a few seconds and try again.",
        };
      }

      return {
        success: false,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error.message ||
          "Signup failed",
      };
    } finally {
      setLoading(false);
    }
  };
  
  const forgotPassword = async (email) => {
    try {
      const res = await API.post("/auth/forgot-password", { email });
      return { success: true, message: res.data.message };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.detail || "Failed to send reset code."
      };
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const res = await API.post("/auth/verify-otp", { email, otp });
      return { success: true, token: res.data.reset_token };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.detail || "Invalid code."
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const res = await API.post("/auth/reset-password", { token, new_password: newPassword });
      return { success: true, message: res.data.message };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.detail || "Failed to reset password."
      };
    }
  };

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      const result = await fetchAndStoreUser(storedToken);
      if (result?.suspended || result?.maintenance) {
        logout();
      }
    }
  }, [logout]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "token" && !event.newValue) {
        logout();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () =>
      window.removeEventListener("storage", handleStorageChange);
  }, [logout]);

  const value = useMemo(
    () => ({
      currentUser,
      token,
      login,
      signup,
      logout,
      loading,
      initialized,
      isAuthenticated: Boolean(token && currentUser),
      forgotPassword, 
      verifyOtp,      
      resetPassword,
      refreshUser,
    }),
    [currentUser, token, login, signup, logout, loading, initialized, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {initialized ? children : null}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export default AuthContext;
